<template>
    <div 
        class="printer-item" 
        :class="{ 
            'printer-online': status, 
            'printer-offline': !status,
            'selectable': selectable,
            'selected': selectable && selected
        }"
        @click="handleClick"
    >
        <div class="printer-content">
            <div class="printer-icon">
                <v-avatar :color="status ? 'success' : 'error'" size="28">
                    <v-icon color="white" size="16">
                        {{ status ? 'mdi-printer' : 'mdi-printer-off' }}
                    </v-icon>
                </v-avatar>
            </div>
            
            <div class="printer-info">
                <div class="printer-name">{{ name }}</div>
                <div class="printer-status">
                    <span 
                        class="status-text"
                        :class="{ 'online': status, 'offline': !status }"
                    >
                        {{ status ? 'Online' : 'Offline' }}
                    </span>
                </div>
            </div>

            <!-- Selection Radio Button (only in selectable mode) -->
            <div v-if="selectable" class="printer-selection">
                <v-radio 
                    :input-value="selected"
                    color="primary" 
                    class="ma-0"
                    readonly
                ></v-radio>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PrinterItem',
    props: {
        status: {
            type: Boolean,
            required: true
        },
        name: {
            type: String,
            required: true
        },
        selectable: {
            type: Boolean,
            default: false
        },
        selected: {
            type: Boolean,
            default: false
        }
    },
    methods: {
        handleClick(event) {
            console.log('🖱️ PrinterItem clicked:', this.name, 'selectable:', this.selectable);
            console.log('🖱️ Click event:', event);
            
            if (this.selectable) {
                console.log('🖱️ Emitting click event for selectable printer');
                this.$emit('click');
            } else {
                console.log('🖱️ Emitting click event for non-selectable printer');
                this.$emit('click');
            }
        }
    }
}
</script>

<style scoped>
.printer-item {
    background: linear-gradient(135deg, #ffffff, #f8f9fa);
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    padding: 8px 12px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
    min-height: 48px;
    pointer-events: auto;
    user-select: none;
}

.printer-item.selectable {
    cursor: pointer;
}

.printer-item:not(.selectable) {
    cursor: default;
}

.printer-item.selectable:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.printer-item.selectable.selected {
    background: linear-gradient(135deg, #4caf50, #66bb6a);
    border-color: #388e3c;
    color: white;
    box-shadow: 0 3px 12px rgba(76, 175, 80, 0.4);
}

.printer-item.selectable.selected .printer-name {
    color: white;
}

.printer-item.selectable.selected .status-text {
    color: rgba(255, 255, 255, 0.9);
}

/* Non-selectable items have no hover effects */

.printer-item.printer-online {
    border-color: #4caf50;
    background: linear-gradient(135deg, #f1f8e9, #ffffff);
}

.printer-item.printer-offline {
    border-color: #f44336;
    background: linear-gradient(135deg, #ffebee, #ffffff);
}

.printer-item.selected {
    border-color: #1976d2;
    background: linear-gradient(135deg, rgba(25, 118, 210, 0.05), #ffffff);
    box-shadow: 0 3px 10px rgba(25, 118, 210, 0.2);
}

.printer-content {
    display: flex;
    align-items: center;
    gap: 12px;
}

.printer-icon {
    flex-shrink: 0;
}

.printer-info {
    flex: 1;
    min-width: 0;
}

.printer-name {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.printer-status {
    display: flex;
    align-items: center;
}

.status-text {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.status-text.online {
    color: #4caf50;
}

.status-text.offline {
    color: #f44336;
}

.printer-selection {
    flex-shrink: 0;
    display: flex;
    align-items: center;
}

.printer-indicator {
    flex-shrink: 0;
    display: flex;
    align-items: center;
}

.status-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    animation: pulse 2s infinite;
}

.status-dot.online {
    background-color: #4caf50;
    box-shadow: 0 0 8px rgba(76, 175, 80, 0.6);
}

.status-dot.offline {
    background-color: #f44336;
    box-shadow: 0 0 8px rgba(244, 67, 54, 0.6);
    animation: none;
}

.connection-quality {
    position: absolute;
    top: 8px;
    right: 8px;
}

.signal-bars {
    display: flex;
    align-items: flex-end;
    gap: 2px;
    height: 16px;
}

.bar {
    width: 3px;
    background-color: #4caf50;
    border-radius: 1px;
    animation: signal-strength 2s ease-in-out infinite;
}

.bar-1 { height: 25%; animation-delay: 0s; }
.bar-2 { height: 50%; animation-delay: 0.2s; }
.bar-3 { height: 75%; animation-delay: 0.4s; }
.bar-4 { height: 100%; animation-delay: 0.6s; }

/* Animations */
@keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
}

@keyframes signal-strength {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .printer-item {
        padding: 12px;
    }
    
    .printer-name {
        font-size: 14px;
    }
    
    .printer-content {
        gap: 8px;
    }
    
    .connection-quality {
        display: none; /* Hide signal bars on mobile for space */
    }
}

@media (max-width: 480px) {
    .printer-item {
        padding: 10px;
    }
    
    .printer-name {
        font-size: 13px;
    }
    
    .status-dot {
        width: 10px;
        height: 10px;
    }
}
</style>